import { supabase } from '../services/supabase';
import { markMobileHandoffStarted } from './mobileHandoffGuard';

const CALLBACK_SEGMENT = 'auth/callback';

const isRefreshTokenAlreadyUsed = (error: unknown): boolean => {
  const code = (
    (error as { code?: unknown; error_code?: unknown } | null)?.code ||
    (error as { error_code?: unknown } | null)?.error_code ||
    ''
  )
    .toString()
    .toLowerCase();
  const message = (
    (error as { message?: unknown; error_description?: unknown } | null)?.message ||
    (error as { error_description?: unknown } | null)?.error_description ||
    ''
  )
    .toString()
    .toLowerCase();
  return code === 'refresh_token_already_used' || message.includes('already used');
};

/**
 * Rotate once immediately before handoff so the app receives an unused token
 * pair. A stale Chrome/Custom Tabs session is the usual source of
 * invalid_grant on mobile refresh-only import.
 */
const mintHandoffSession = async (refreshToken: string) => {
  try {
    supabase.auth.stopAutoRefresh();
  } catch {
    // continue; refreshSession still works without this
  }

  const { data, error } = await supabase.auth.refreshSession({
    refresh_token: refreshToken,
  });
  if (data?.session?.refresh_token) {
    return data.session;
  }

  if (isRefreshTokenAlreadyUsed(error)) {
    const { data: current } = await supabase.auth.getSession();
    if (current?.session?.refresh_token) {
      return current.session;
    }
  }

  return null;
};

const TRUSTED_NATIVE_SCHEMES = new Set([
  'churchapp:',
  'com.alwinsley.newchurchoffaith:',
]);

const TRUSTED_EXPO_SCHEMES = new Set(['exp:', 'exps:']);

const hasAuthCallbackPath = (url: URL) => {
  const combinedPath = `${url.host}${url.pathname}`.toLowerCase();
  return combinedPath.includes(CALLBACK_SEGMENT);
};

/**
 * Accept only known mobile callback targets for token handoff.
 * This blocks arbitrary redirect_to values from receiving auth tokens.
 */
export const getTrustedMobileRedirect = (value: string | null): string | null => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }

  const protocol = parsed.protocol.toLowerCase();
  const callbackPathValid = hasAuthCallbackPath(parsed);
  if (!callbackPathValid) return null;

  if (TRUSTED_NATIVE_SCHEMES.has(protocol)) {
    return trimmed;
  }

  if (TRUSTED_EXPO_SCHEMES.has(protocol)) {
    // Expo Go callbacks usually include /--/auth/callback. Keep this strict.
    const path = parsed.pathname.toLowerCase();
    if (path.includes('/--/auth/callback') || path.endsWith('/auth/callback')) {
      return trimmed;
    }
  }

  return null;
};

export const DEFAULT_MOBILE_APP_CALLBACK = 'churchapp://auth/callback';
const PASSWORD_RESET_MOBILE_REDIRECT_KEY = 'ncof.password-reset.mobile-redirect';

export const isLikelyMobileDevice = (
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''
): boolean => /iPhone|iPad|iPod|Android/i.test(userAgent);

/**
 * Password-reset emails land on the web app. If the request came from the
 * mobile app (trusted redirect_to) or the user is on a phone, hand the new
 * session back to the native app instead of staying on ncof.app/login.
 */
export const resolvePasswordResetMobileRedirect = (
  redirectToParam: string | null,
  userAgent?: string
): string | null => {
  const trusted = getTrustedMobileRedirect(redirectToParam);
  if (trusted) return trusted;
  if (isLikelyMobileDevice(userAgent)) return DEFAULT_MOBILE_APP_CALLBACK;
  return null;
};

const readStoredMobileRedirect = (): string | null => {
  try {
    return getTrustedMobileRedirect(sessionStorage.getItem(PASSWORD_RESET_MOBILE_REDIRECT_KEY));
  } catch {
    return null;
  }
};

const storeMobileRedirect = (value: string) => {
  try {
    sessionStorage.setItem(PASSWORD_RESET_MOBILE_REDIRECT_KEY, value);
  } catch {
    // Private mode can block sessionStorage; URL/UA fallback still works.
  }
};

/**
 * Keep the mobile callback across token-hash cleanup and reloads so a reset
 * that started in the app still hands back after the password is changed.
 */
export const capturePasswordResetMobileRedirect = (
  search = typeof window !== 'undefined' ? window.location.search : '',
  userAgent?: string
): string | null => {
  const params = new URLSearchParams(search);
  const resolved = resolvePasswordResetMobileRedirect(params.get('redirect_to'), userAgent);
  if (resolved) {
    storeMobileRedirect(resolved);
    return resolved;
  }
  return readStoredMobileRedirect();
};

/**
 * Hand a freshly minted session to the mobile app, then clear only this browser.
 *
 * Local sign-out does not revoke the refresh token on the server, so the app can
 * still use it. Clearing web storage prevents both clients from sharing one
 * refresh-token family and racing on rotation.
 */
export const redirectToMobileHandoff = async (
  mobileRedirect: string,
  refreshToken: string
): Promise<void> => {
  // Mark before local sign-out so LoginPage does not immediately auto-login
  // again and rotate a new refresh token out from under the app.
  markMobileHandoffStarted();

  const session = await mintHandoffSession(refreshToken);
  const accessToken = session?.access_token;
  const nextRefreshToken = session?.refresh_token;

  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    // Continue with handoff even if local cleanup fails.
  }

  if (!nextRefreshToken) {
    throw new Error('Your web session expired. Please sign in again to return to the app.');
  }

  const hashParts = [`refresh_token=${encodeURIComponent(nextRefreshToken)}`];
  if (accessToken) {
    hashParts.unshift(`access_token=${encodeURIComponent(accessToken)}`);
  }
  const handoffUrl = `${mobileRedirect}#${hashParts.join('&')}`;
  window.location.replace(handoffUrl);
  setTimeout(() => {
    try {
      // Only retry if this tab is still visible. A second hop after the app
      // already consumed the refresh token races GoTrue rotation.
      if (document.visibilityState !== 'visible') return;
      window.location.href = handoffUrl;
    } catch {
      // ignore
    }
  }, 250);
};

